
import React, { useState, CSSProperties } from "react";
import { Campaign, Quest, QuestBoard } from "@dndModels";
import DandDAPI from "@utilities/smart_api";
import EditButton from "@components/input/buttons/icon_buttons/EditButton";
import FlexBox from "@components/layout/FlexBox";
import AddButton from "@components/input/buttons/icon_buttons/AddButton";
import StringBox from "@components/input/strings/StringBox";
import QuestCard from "./QuestCard";
import QuestInput from "./QuestInput";

// ─── Types ────────────────────────────────────────────────────────────────────


interface QuestBoardProps {
  campaign: Campaign;
  quest_board: QuestBoard;
  edit?: boolean;
}

const styles: Record<string, CSSProperties> = {
  page: {
    margin: 0,
    fontFamily: "'EB Garamond', serif",
    background:
      "radial-gradient(ellipse at top, #6b4a2f, #4a3220 60%, #2b1b12 100%)",
    color: "#2e2014",
    padding: "32px 16px 64px",
    minHeight: "100vh",
    boxSizing: "border-box",
  },
  boardFrame: {
    maxWidth: 1600,
    margin: "0 auto",
    background:
      "repeating-linear-gradient(90deg, #4a3220 0px, #6b4a2f 3px, #4a3220 6px)",
    border: "14px solid #2b1b12",
    borderRadius: 6,
    boxShadow:
      "0 0 0 4px #6b4a2f inset, 0 25px 60px rgba(0,0,0,0.6), 0 0 40px rgba(0,0,0,0.5) inset",
    padding: "28px 22px 36px",
    position: "relative",
    boxSizing: "border-box",
  },
  innerBorder: {
    position: "absolute",
    inset: 8,
    border: "2px solid rgba(0,0,0,0.35)",
    borderRadius: 4,
    pointerEvents: "none",
  },
  header: {
    textAlign: "center",
    marginBottom: 8,
  },
  crest: {
    width: 64,
    height: 64,
    display: "block",
    margin: "0 auto 6px",
    opacity: 0.9,
  },
  h1: {
    fontFamily: "'Cinzel Decorative', serif",
    fontWeight: 900,
    color: "#ecdfbd",
    fontSize: "clamp(1.6rem, 4vw, 2.6rem)",
    textShadow: "0 2px 0 #000, 0 0 18px rgba(184,137,58,0.5)",
    margin: "0 0 4px",
    letterSpacing: 1,
  },
  subtitle: {
    fontFamily: "'Cinzel', serif",
    color: "#b8893a",
    fontSize: "0.95rem",
    letterSpacing: 3,
    textTransform: "uppercase",
    marginBottom: 18,
    textShadow: "0 1px 0 #000",
  },
  magistrateNote: {
    maxWidth: 640,
    margin: "0 auto 26px",
    textAlign: "center",
    color: "#dccb9e",
    fontStyle: "italic",
    fontSize: "1.02rem",
    lineHeight: 1.5,
    padding: "0 12px",
  },
  tally: {
    textAlign: "center",
    fontFamily: "'Cinzel', serif",
    color: "#ecdfbd",
    fontSize: "0.95rem",
    letterSpacing: 1,
    marginBottom: 24,
  },
  tallyCount: {
    color: "#b8893a",
    fontWeight: 700,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))",
    gap: 22,
  },
  questBase: {
    position: "relative",
    background:
      "radial-gradient(ellipse at 30% 0%, rgba(255,255,255,0.25), transparent 60%), linear-gradient(160deg, #ecdfbd 0%, #dccb9e 100%)",
    borderRadius: 3,
    padding: "20px 18px 18px",
    boxShadow: "0 8px 18px rgba(0,0,0,0.45), 0 0 0 1px rgba(0,0,0,0.15)",
    // clipPath: "polygon(2% 4%, 98% 0%, 100% 96%, 3% 100%)",
    transition: "transform 0.25s ease, box-shadow 0.25s ease, filter 0.4s ease",
    boxSizing: "border-box",
  },
  pin: {
    position: "absolute",
    top: -10,
    left: "50%",
    transform: "translateX(-50%)",
    width: 16,
    height: 16,
    borderRadius: "50%",
    background:
      "radial-gradient(circle at 35% 30%, #e8c466, #b8893a 60%, #7a541c)",
    boxShadow: "0 3px 4px rgba(0,0,0,0.5)",
    zIndex: 3,
  },
  questTitle: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.08rem",
    color: "#2e2014",
    lineHeight: 1.25,
  },
  questTitleCompleted: {
    textDecoration: "line-through",
    textDecorationColor: "#5e1c1c",
    textDecorationThickness: "2px",
  },
  questBody: {
    fontSize: "0.98rem",
    lineHeight: 1.45,
    marginBottom: 12,
    color: "#3a2a18",
  },
  questMeta: {
    display: "flex",
    borderTop: "1px dashed rgba(46,32,20,0.35)",
    paddingTop: 10,
    fontSize: "0.88rem",
  },
  reward: {
    fontStyle: "italic",
    color: "#5e1c1c",
  },
  rewardBold: {
    fontStyle: "normal",
    fontWeight: 700,
  },
  sealBtn: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    background:
      "radial-gradient(circle at 35% 30%, #b94a4a, #8b2a2a 55%, #5e1c1c 100%)",
    boxShadow:
      "0 3px 6px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.15)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "rgba(255,255,255,0.85)",
    transition: "transform 0.15s ease",
  },
  claimBtn: {
    width: 46,
    height: 46,
    borderRadius: "50%",
    border: "none",
    cursor: "pointer",
    background:
      "radial-gradient(circle at 35% 30%,rgb(9, 65, 168),rgb(47, 57, 104) 55%,rgb(26, 101, 122) 100%)",
    boxShadow:
      "0 3px 6px rgba(0,0,0,0.5), inset 0 0 0 2px rgba(255,255,255,0.15)",
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "1.2rem",
    fontWeight: "bold",
    color: "rgba(255,255,255,0.85)",
    transition: "transform 0.15s ease",
  },
  completedStamp: {
    position: "absolute",
    top: "38%",
    left: "50%",
    transform: "translate(-50%, -50%) rotate(-12deg)",
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  hiddenStamp: {
    position: "absolute",
    top: "38%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  claimedStamp: {
    fontFamily: "'Cinzel', serif",
    fontWeight: 700,
    fontSize: "1.6rem",
    color: "#8b2a2a",
    border: "4px solid #8b2a2a",
    padding: "2px 14px",
    borderRadius: 4,
    opacity: 0.55,
    letterSpacing: 2,
    pointerEvents: "none",
    whiteSpace: "nowrap",
  },
  grandPrizeBase: {
    maxWidth: 520,
    margin: "26px auto 0",
    textAlign: "center",
    background: "linear-gradient(160deg, #ecdfbd, #dccb9e)",
    borderRadius: 4,
    padding: "14px 18px",
    // clipPath: "polygon(1% 8%, 99% 0%, 100% 92%, 0% 100%)",
    transition: "box-shadow 0.4s ease",
  },
  grandPrizeH3: {
    fontFamily: "'Cinzel', serif",
    margin: "0 0 4px",
    color: "#5e1c1c",
    fontSize: "1rem",
    letterSpacing: 1,
  },
  grandPrizeP: {
    margin: 0,
    fontSize: "0.95rem",
    color: "#2e2014",
  },
  footerNote: {
    textAlign: "center",
    marginTop: 30,
    color: "#dccb9e",
    fontFamily: "'Cinzel', serif",
    fontSize: "0.85rem",
    letterSpacing: 1,
  },
};

// ─── Font injection ────────────────────────────────────────────────────────────

if (typeof document !== "undefined" && !document.getElementById("quest-board-fonts")) {
  const link = document.createElement("link");
  link.id = "quest-board-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@700;900&family=Cinzel:wght@600;700&family=EB+Garamond:ital,wght@0,400;0,600;1,400&display=swap";
  document.head.appendChild(link);
}

// ─── Components ───────────────────────────────────────────────────────────────


const api = DandDAPI.getInstance();

export default function DisplayQuestBoard({ campaign, quest_board, edit = false }: QuestBoardProps) {
  const [ping, setPing] = useState(false);

  const quests = quest_board.quests;

  return (
    <div style={ edit ? {} : styles.page }>
      <div style={ edit ? {} : styles.boardFrame}>
        <div style={ edit ? {} : styles.innerBorder} />

        <header style={ edit ? {} : styles.header}>
          <FlexBox direction="row">
            <FlexBox>
              { edit ? 
                <div style={{ backgroundColor: "black" }}>
                  <StringBox 
                    name="Name"
                    value={quest_board.name}
                    onBlur={ async (changed: string) => {
                      quest_board.name = changed;
                      api.upsertObject("campaign_quest_board", quest_board);
                    }}
                  />
                </div>
              : 
                <h1 style={styles.h1}>{ quest_board.name }</h1> 
              }
            </FlexBox>
          </FlexBox>
          <FlexBox direction="row">
            <FlexBox>
              { edit ? 
                <div style={{ backgroundColor: "black" }}>
                  <StringBox 
                    name="Subheader"
                    value={quest_board.subheader}
                    multiline
                    onBlur={ async (changed: string) => {
                      quest_board.subheader = changed;
                      api.upsertObject("campaign_quest_board", quest_board);
                    }}
                  />
                </div>
              : 
                <div style={styles.subtitle}>
                  { quest_board.subheader }
                </div> 
              }
            </FlexBox>
          </FlexBox>
        </header>

        <div style={ edit ? {} : styles.magistrateNote}>
          <FlexBox direction="row">
            <FlexBox>
              { edit ? 
                <div style={{ backgroundColor: "black" }}>
                  <StringBox 
                    name="Board Note"
                    value={quest_board.board_note}
                    multiline
                    onBlur={ async (changed: string) => {
                      quest_board.board_note = changed;
                      api.upsertObject("campaign_quest_board", quest_board);
                    }}
                  />
                </div>
              : quest_board.board_note 
              }
            </FlexBox>
          </FlexBox>
        </div>

        <div style={ edit ? {} : styles.tally}>
          Quests completed:{" "}
          <span style={styles.tallyCount}>{quests.filter(o => o.completed && o.show).length}</span> /{" "}
          <span style={styles.tallyCount}>{quests.filter(o => o.show).length}</span>
        </div>

        { edit &&
          <FlexBox>
            <AddButton fab 
              onClick={ async () => {
                const new_quest = new Quest();
                quest_board.quests.push(new_quest);
                api.upsertObject("campaign_quest_board", quest_board);
                setPing(!ping);
              }} 
            />
          </FlexBox>
        }
        { edit ? 
          <FlexBox direction="column">
            {quests.map(quest => {
              return (
                <FlexBox key={quest.true_id} className="padded">
                  <QuestInput
                    campaign={campaign}
                    quest={quest}
                    onChange={() => {
                      // debugger;
                      api.upsertObject("campaign_quest_board", quest_board);
                      setPing(!ping);
                    }}
                    onDelete={() => {
                      if (edit) {
                        quest_board.quests = quest_board.quests.filter(o => o.true_id !== quest.true_id);
                        api.upsertObject("campaign_quest_board", quest_board);
                        setPing(!ping);
                      }
                    }}
                  />
                </FlexBox>
              );
            })}
          </FlexBox>
        :
          <div style={styles.grid}>
            {quests.filter(o => o.show).map((quest, i) => (
              <QuestCard
                key={i}
                campaign={campaign}
                quest={quest}
                index={i}
                enabled={false}
                onChange={() => {
                  // debugger;
                  api.upsertObject("campaign_quest_board", quest_board);
                  setPing(!ping);
                }}
              />
            ))}
          </div>
        }
      </div>
    </div>
  );
}
